import axios from 'axios'
import { API_BASE_URL, APP_ENV, IS_PRODUCTION } from '@/lib/config'
import log from '@/lib/logger'

/**
 * Instância axios central.
 * - Dev: baseURL relativa (`/api`); o Vite faz proxy para o backend PHP.
 * - Produção: `VITE_API_URL` (https://faturamento.sisprecisa.com.br/api).
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

log.info('api', 'Cliente HTTP inicializado', {
  baseURL: API_BASE_URL,
  modo: APP_ENV,
  producao: IS_PRODUCTION,
  origem: typeof window !== 'undefined' ? window.location.origin : null,
})

const TOKEN_KEY = 'faturamento.token'

export function getToken() {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
    log.debug('auth', 'Token gravado no localStorage', { tamanho: token.length })
  } else {
    localStorage.removeItem(TOKEN_KEY)
    log.debug('auth', 'Token removido do localStorage')
  }
}

let seq = 0

// Injeta o token JWT em todas as requisições e marca o início para medir a duração.
api.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) config.headers.Authorization = `Bearer ${token}`

    config.metadata = { id: ++seq, start: performance.now() }
    log.debug('http →', `#${config.metadata.id} ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`, {
      autenticado: !!token,
      params: config.params,
      // Nunca loga a senha em claro.
      body: sanitize(config.data),
    })
    return config
  },
  (error) => {
    log.error('http', 'Falha ao montar a requisição', error)
    return Promise.reject(error)
  }
)

// Loga a resposta; em 401, limpa o token e redireciona ao login.
api.interceptors.response.use(
  (res) => {
    const { id, start } = res.config.metadata ?? {}
    log.debug('http ←', `#${id} ${res.status} ${res.config.method?.toUpperCase()} ${res.config.url}`, {
      ms: start ? Math.round(performance.now() - start) : null,
      requestId: res.headers?.['x-request-id'],
    })
    return res
  },
  (error) => {
    log.http('http ✗', error)

    // Sem `response` = a requisição não chegou ao servidor (DNS, CORS, TLS, rede).
    if (!error.response) {
      log.error(
        'http',
        'A resposta não chegou ao navegador. Verifique se a API responde em ' +
          `${API_BASE_URL}/health, e também CORS/HTTPS/rewrite do servidor.`
      )
    }

    if (error?.response?.status === 401) {
      log.warn('auth', '401 recebido — encerrando a sessão local e voltando ao login')
      setToken(null)
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

/** Remove campos sensíveis antes de logar o corpo da requisição. */
function sanitize(data) {
  if (!data || typeof data !== 'object') return data
  if (typeof FormData !== 'undefined' && data instanceof FormData) return '[FormData]'
  const clone = { ...data }
  for (const key of ['senha', 'senhaConfirmacao', 'senhaAtual', 'certificadoSenha', 'password']) {
    if (key in clone) clone[key] = '***'
  }
  return clone
}

export default api
